import { useState } from "react";

import { useCompanySummary } from "../lib/queries";
import { formatSiren, isValidSiren } from "../lib/siren";
import { CompanySearch } from "./CompanySearch";
import { SirenField } from "./SirenField";

interface Props {
  siren: string;
  onChange: (siren: string) => void;
  size?: "large" | "compact";
  autoFocus?: boolean;
}

/** SIREN entry with name search as an alternative, and a preview of the company found. */
export function CompanyPicker({ siren, onChange, size = "large", autoFocus }: Props) {
  const [byName, setByName] = useState(false);
  const valid = isValidSiren(siren);
  const summary = useCompanySummary(valid ? siren : null);

  return (
    <div className="picker">
      {byName ? (
        <CompanySearch
          autoFocus
          onSelect={(company) => {
            onChange(company.siren);
            setByName(false);
          }}
        />
      ) : (
        <SirenField
          label="Numéro SIREN"
          hint="9 chiffres. Vous pouvez coller un SIRET, nous gardons les 9 premiers."
          value={siren}
          onChange={onChange}
          size={size}
          autoFocus={autoFocus}
          error={valid && summary.isError ? summary.error.message : null}
        />
      )}
      <div className="picker__row">
        <button type="button" className="button button--link" onClick={() => setByName((v) => !v)}>
          {byName ? "Saisir un numéro SIREN" : "Je ne connais pas le SIREN : chercher par nom"}
        </button>
      </div>
      <div className="picker__status" aria-live="polite">
        {valid && summary.isFetching && !summary.data && "Recherche de l'entreprise"}
        {valid && summary.data && (
          <div className="company-preview">
            <div>
              <div className="company-preview__name">{summary.data.name}</div>
              <div className="company-preview__meta">
                SIREN {formatSiren(summary.data.siren)}
                {summary.data.city && `, ${summary.data.postal_code ?? ""} ${summary.data.city}`}
              </div>
            </div>
            <span
              key={summary.data.siren}
              className={`stamp stamp--press ${summary.data.active ? "stamp--ok" : "stamp--critical"}`}
            >
              {summary.data.active ? "Entreprise active" : "Entreprise cessée"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function usePickedCompany(siren: string) {
  const valid = isValidSiren(siren);
  const summary = useCompanySummary(valid ? siren : null);
  return { ready: valid && Boolean(summary.data), company: summary.data ?? null };
}
